import { useState } from 'react';
import { useLoaderData } from 'react-router';
import { ArrowUpRight } from 'lucide-react';
import { useTracker } from '@archipro-website/tracker';

import { useAppDisplayModeContext } from '@archipro-design/aria';
import { useEnquiryVisibility } from '~/modules/enquiry/hooks/use-enquiry-visibility';
import { parsePhone } from '~/modules/root/util/phone';
import type { loader } from '~/routes/_app.product.$urlSegment';
import { TrackedWebsiteCta, detailCta } from '~/ui-rocco/pages/detail-shared';
import { useProductEnquiryModal } from '../hooks/use-product-enquiry-modal';

// React only honours `autoFocus` on form controls, never anchors — so the
// number link that replaces the reveal button is focused imperatively.
const focusOnMount = (node: HTMLAnchorElement | null) => {
    node?.focus();
};

export interface ProductEnquiryPromptProps {
    /** Heading, e.g. "Have a question about this product?". */
    heading: string;
    /** Supporting line beneath the heading. `{supplier}` is substituted. */
    body: string;
    /**
     * Show a click-to-reveal phone number as a secondary action (the mid-page
     * hook uses this — a phone reveal is a tracked conversion). Hidden when the
     * professional has no number.
     */
    showPhone?: boolean;
}

/**
 * The product page's mid-page enquiry hook: a heading, a supporting line
 * naming the supplier, a primary Enquire CTA, a "visit their website" referral
 * and a click-to-reveal phone — the QDD actions where spec questions arise.
 * On desktop it renders as bare editorial-statement row content for the
 * accordion's "Get in Touch" fallback (when no specification representative is
 * set up); on mobile as the standalone soft grey card.
 *
 * Manufactures the reason to enquire (pricing, specification, lead times) that
 * a generic "Get in Touch" leaves the user to invent. Self-hides when the
 * professional accepts no enquiries and has no website.
 */
export const ProductEnquiryPrompt = ({
    heading,
    body,
    showPhone = false,
}: ProductEnquiryPromptProps) => {
    const { product, primaryCTA } = useLoaderData<typeof loader>();
    const professional = product.Professional;
    const { showEnquiryCTA } = useEnquiryVisibility(professional);
    const { openEnquiryModal } = useProductEnquiryModal();
    const { mobile } = useAppDisplayModeContext();

    // The enquiry CTA label follows the Product Call-to-Action setting —
    // the same source the main purchase CTA uses — rather than a hardcoded
    // "Enquire" or the brand-profile Primary CTA.
    const enquireLabel = primaryCTA.primaryCTALabelTitleCase || 'Enquire';

    // Phone reveal is a tracked conversion (lead capture). It starts hidden on
    // every page load — local state, not session-persisted — so the number is
    // always behind a deliberate click that logs PhoneReveal; showing it up
    // front would skip the click and capture no lead. Starting `false` also
    // matches the server render, so there is no hydration mismatch. Prefer the
    // professional's tracking number when set.
    const phoneNumber = professional.PhoneTracking || professional.Phone;
    const { log } = useTracker();
    const [isRevealed, setIsRevealed] = useState(false);
    const onRevealClick = () => {
        setIsRevealed(true);
        log('PhoneReveal', {
            url: new URL(window.location.href),
            professionalId: professional.ID,
            data: { ProfessionalID: professional.ID },
        });
    };
    const telHref =
        parsePhone(phoneNumber ?? '', professional.Country ?? undefined)
            ?.telUri ?? `tel:${phoneNumber}`;

    const websiteUrl = product.Website || professional.Website;
    // Nothing to offer → render nothing.
    if (!showEnquiryCTA && !websiteUrl) return null;

    const supplier = professional.Title;
    const bodyText = body.replace('{supplier}', supplier);
    // The phone is a contact channel like the enquiry form — it respects the
    // professional's enquiry audience alongside the Enquire CTA.
    const showPhoneReveal = showPhone && showEnquiryCTA && Boolean(phoneNumber);

    // Desktop hook — bare row content for the accordion's "Get in Touch"
    // fallback, in the page's display voice (the tagged-projects treatment:
    // a TWK Ghost statement). The question carries the ink; the supplier's
    // answer continues in grey; the actions sit quietly beneath. The row
    // header supplies the numbering and title, so there is no eyebrow.
    if (!mobile) {
        return (
            <div className="pb-[1.8rem] text-[#181818]">
                <p className="m-0 max-w-[64rem] font-twk-ghost text-[3rem] leading-[1.02] font-normal tracking-[-0.02em] text-balance">
                    {heading} <span className="text-[#8f8f8f]">{bodyText}</span>
                </p>
                <div className="mt-[2.5rem] flex items-center gap-3">
                    {showEnquiryCTA ? (
                        <button
                            type="button"
                            onClick={() =>
                                openEnquiryModal({
                                    title: `${enquireLabel} with ${supplier}`,
                                    ctaLabel: `${enquireLabel} with ${supplier}`,
                                })
                            }
                            className={detailCta('filled-light')}
                        >
                            {enquireLabel}
                        </button>
                    ) : null}
                    {/* Website referral (worth a QDD point) is the prominent
                            secondary; the phone reveal (a tracked conversion,
                            but not QDD) is the lighter sub-link beside it. */}
                    <TrackedWebsiteCta
                        website={websiteUrl}
                        libraryItemID={product.ID}
                        professionalID={professional.ID}
                        professionalTitle={supplier}
                        variant="outlined-light"
                    >
                        Visit Website
                        <ArrowUpRight className="size-4" />
                    </TrackedWebsiteCta>
                    {showPhoneReveal ? (
                        isRevealed ? (
                            <span className="ml-2 font-sans text-sm text-[#767676]">
                                Prefer to call?{' '}
                                <a
                                    href={telHref}
                                    // The reveal button unmounts on click;
                                    // hand keyboard focus to the number.
                                    ref={focusOnMount}
                                    className="font-medium text-[#1f1f21] underline underline-offset-2"
                                >
                                    {phoneNumber}
                                </a>
                            </span>
                        ) : (
                            <button
                                type="button"
                                onClick={onRevealClick}
                                className="ml-2 inline-flex items-center gap-1 font-sans text-sm text-[#767676] underline underline-offset-2"
                            >
                                Prefer to call? Show number
                            </button>
                        )
                    ) : null}
                </div>
            </div>
        );
    }

    return (
        // px-6, the phone page's one gutter — the card's edge sits where
        // every other band starts.
        <section className="bg-ground px-6">
            {/* White, not the old #f5f5f6: that grey was the card against the
                white page this block was designed on. On the page's grey
                ground it vanished, leaving its padding reading as a hole in
                the flow rather than as a card's inset. */}
            <div className="flex flex-col gap-5 rounded-2xl bg-white p-6">
                <div className="flex flex-col gap-2">
                    <h3 className="m-0 font-sans text-[1.375rem] leading-[1.15] font-medium tracking-[-0.02em] text-[#181818]">
                        {heading}
                    </h3>
                    <p className="m-0 font-sans text-[0.9375rem] leading-[1.45] text-[#5c5c63]">
                        {bodyText}
                    </p>
                </div>
                <div className="flex flex-col items-start gap-3">
                    {showEnquiryCTA ? (
                        <button
                            type="button"
                            onClick={() =>
                                openEnquiryModal({
                                    title: `${enquireLabel} with ${supplier}`,
                                    ctaLabel: `${enquireLabel} with ${supplier}`,
                                })
                            }
                            className={detailCta(
                                'filled-light',
                                'w-full text-sm',
                            )}
                        >
                            {enquireLabel}
                        </button>
                    ) : null}
                    {/* Website referral (worth a QDD point) is the prominent
                        secondary; the phone reveal (a tracked conversion, but
                        not QDD) drops to the lighter sub-link below it. */}
                    <TrackedWebsiteCta
                        website={websiteUrl}
                        libraryItemID={product.ID}
                        professionalID={professional.ID}
                        professionalTitle={supplier}
                        variant="outlined-light"
                        className="w-full text-sm"
                    >
                        Visit Website
                        <ArrowUpRight className="size-4" />
                    </TrackedWebsiteCta>
                    {showPhoneReveal ? (
                        isRevealed ? (
                            <span className="font-sans text-sm text-[#767676]">
                                Prefer to call?{' '}
                                <a
                                    href={telHref}
                                    // The reveal button unmounts on click;
                                    // hand keyboard focus to the number.
                                    ref={focusOnMount}
                                    className="font-medium text-[#1f1f21] underline underline-offset-2"
                                >
                                    {phoneNumber}
                                </a>
                            </span>
                        ) : (
                            <button
                                type="button"
                                onClick={onRevealClick}
                                className="inline-flex items-center gap-1 font-sans text-sm text-[#767676] underline underline-offset-2"
                            >
                                Prefer to call? Show number
                            </button>
                        )
                    ) : null}
                </div>
            </div>
        </section>
    );
};
